<script lang="ts" setup>
import type { SelectOption } from 'naive-ui'
import type { ApiCustomerModuleDefectUpsertPayload } from '~/layers/customer-module/types'

const props = defineProps<{
  mode: 'create' | 'update'
  payload: ApiCustomerModuleDefectUpsertPayload
  positionOptions: SelectOption[]
  defectTypeOptions: SelectOption[]
  defaultAlternativeContactPerson: { name: string, phone: string | null, email?: string | null } | undefined
  isFormDisabled: boolean
}>()
defineEmits<{ (e: 'submit', payload: ApiCustomerModuleDefectUpsertPayload): void }>()
const { cloned: formData } = useCloned(props.payload)

const isUpdateMode = computed(() => props.mode === 'update')

const selectedPositionId = ref<string | undefined | null>(formData.value.machineryId || formData.value.machineryAccessoryId)
watch(selectedPositionId, (id) => {
  if (id) {
    if (id.startsWith('a')) {
      formData.value.machineryId = undefined
      formData.value.machineryAccessoryId = id
      formData.value.type = 'machineryAccessory'
    } else {
      formData.value.machineryAccessoryId = undefined
      formData.value.machineryId = id
      formData.value.type = 'machinery'
    }
  }
})

const selectAlternativeContactPerson = ref(!!formData.value.alternativeContactPersonName)
watch(selectAlternativeContactPerson, (newValue) => {
  if (newValue && props.defaultAlternativeContactPerson) {
    formData.value.alternativeContactPersonName = props.defaultAlternativeContactPerson.name
    formData.value.alternativeContactPersonPhone = props.defaultAlternativeContactPerson.phone
    formData.value.alternativeContactPersonEmail = props.defaultAlternativeContactPerson.email
  } else {
    formData.value.alternativeContactPersonName = undefined
    formData.value.alternativeContactPersonPhone = undefined
    formData.value.alternativeContactPersonEmail = undefined
  }
})
</script>

<template>
  <FormKit
    v-model="formData"
    type="form"
    :disabled="isFormDisabled"
    @submit="$emit('submit', formData)"
  >
    <FormKitMultiSelect
      v-model="selectedPositionId"
      :label="$t('customerModule.defect.id.label')"
      :multi-select-options="positionOptions"
      :is-multi-select-allowed="false"
      validation="required"
      :disabled="isUpdateMode"
    />

    <template v-if="selectedPositionId">
      <FormKit
        name="description"
        :label="$t('customerModule.defect.description.label')"
        type="text"
        validation="required"
      />
      <FormKitFileDropzone
        name="photos"
        :label="$t('customerModule.defect.photos.label')"
        use-public-signed-url-endpoint
      />

      <FormKit
        v-model="selectAlternativeContactPerson"
        type="checkbox"
        name="selectAlternativeContactPerson"
        :label="$t('customerModule.defect.selectAlternativeContactPerson.label')"
      />

      <template v-if="selectAlternativeContactPerson">
        <h2 class="font-bold text-base mb-2">
          {{ $t('customerModule.defect.alternativeContactPerson.label') }}
        </h2>
        <div class="flex flex-col md:flex-row gap-x-4">
          <FormKit
            name="alternativeContactPersonName"
            :label="$t('customerModule.defect.alternativeContactPersonName.label')"
            type="text"
            validation="required"
          />
          <FormKit
            name="alternativeContactPersonEmail"
            :label="$t('customerModule.defect.alternativeContactPersonEmail.label')"
            type="email"
            validation="email"
          />
          <FormKit
            name="alternativeContactPersonPhone"
            :label="$t('customerModule.defect.alternativeContactPersonPhone.label')"
            type="tel"
            validation="required|matches:/^\+?[0-9 /-]+$/"
          />
        </div>
      </template>
    </template>
  </FormKit>
</template>
